import './applicantResidenceDetailsPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: {
      currentResidenceCityAsString,
      currentResidenceLineOneAsString,
      currentResidenceLineTwoAsString,
      currentResidencePostalCodeAsString,
      currentResidenceStateAsString,
      isCurrentResidencePersisted,
    },
    currentResidenceMonthlyPaymentAmount,
    isSyncingResidence,
    syncingResidenceFailedMessage,
    setCurrentResidenceCity,
    setCurrentResidenceLineOne,
    setCurrentResidenceLineTwo,
    setCurrentResidenceMonthlyPaymentAmount,
    setCurrentResidencePostalCode,
    setCurrentResidenceState,
    syncResidence,
  } = useApplicantStore();

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      cityValue: currentResidenceCityAsString,
      housingAmountValue: inputEventHandler.currency.formatter(
        currentResidenceMonthlyPaymentAmount,
      ),
      lineOneValue: currentResidenceLineOneAsString,
      lineTwoValue: currentResidenceLineTwoAsString,
      postalCodeValue: currentResidencePostalCodeAsString,
      stateValue: currentResidenceStateAsString,
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      housingAmountValue,
      lineOneValue,
      lineTwoValue,
      postalCodeValue,
      stateValue,
    } = values;

    if (
      !cityValue ||
      !housingAmountValue ||
      !lineOneValue ||
      !postalCodeValue ||
      !stateValue
    ) {
      return;
    }

    setCurrentResidenceCity(cityValue);
    setCurrentResidenceLineOne(lineOneValue);
    setCurrentResidenceLineTwo(lineTwoValue);
    setCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCurrentResidencePostalCode(postalCodeValue);
    setCurrentResidenceState(stateValue);

    if (isCurrentResidencePersisted && isDirty) {
      const synced = await syncResidence();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-residence-details-page" sx={theme.pageLayout}>
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantResidenceDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantResidenceDetails.form.h1}>
                {t('ApplicantResidenceDetailsPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantResidenceDetails.form.h2}>
                {t('ApplicantResidenceDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingResidenceFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantResidenceDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingResidenceFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('housingAmountValue', housingAmountValidation)}
                    error={!isEmpty(errors.housingAmountValue)}
                    label={t('ApplicantResidenceDetailsPage.housingAmount')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantResidenceDetails.form
                            .amountInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'housingAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'housingAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'housingAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.housingAmountValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.applicantResidenceDetails.form
                          .amountInputError
                      }
                    >
                      {toString(errors.housingAmountValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.applicantResidenceDetails.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t('ApplicantResidenceDetailsPage.residenceLineOne')}
                    name="lineOneValue"
                    register={register}
                    setValue={setValue}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantResidenceDetails.form
                            .addressLine1Input,
                      },
                    }}
                    suggestionFields={{
                      cityFieldName: 'cityValue',
                      stateFieldName: 'stateValue',
                      postalCodeFieldName: 'postalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        label={t(
                          'ApplicantResidenceDetailsPage.residenceLineTwo',
                        )}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.applicantResidenceDetails.form
                                .addressLine2Input,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('cityValue', cityValidation)}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.cityValue)}
                        label={t('ApplicantResidenceDetailsPage.residenceCity')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('cityValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.applicantResidenceDetails.form
                                .cityInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.cityValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.applicantResidenceDetails.form
                              .cityInputError
                          }
                        >
                          {toString(errors.cityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.applicantResidenceDetails.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.applicantResidenceDetails.form
                            .stateSelect
                        }
                        label={t(
                          'ApplicantResidenceDetailsPage.residenceState',
                        )}
                        name="stateValue"
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                        rules={stateValidation}
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('postalCodeValue', postalCodeValidation)}
                        autoComplete="postal-code"
                        error={!isEmpty(errors.postalCodeValue)}
                        label={t('ApplicantResidenceDetailsPage.zip')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('postalCodeValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.applicantResidenceDetails.form
                                .zipCodeInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.postalCodeValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.applicantResidenceDetails.form
                              .zipCodeInputError
                          }
                        >
                          {toString(errors.postalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantResidenceDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingResidence}
                  fullWidth
                  submit
                >
                  {t('ApplicantResidenceDetailsPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantResidenceDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantResidenceDetailsPage';
